import { useNavigate, useParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import PageSearch from '../../molecules/PageSearch';
import { useSignal } from '@preact/signals-react';
import FieldsModal, { FieldItem } from './FieldsModal';
import ParamsModal from './ParamsModal';
import { FaCheck } from 'react-icons/fa6';
import { FaTimes } from 'react-icons/fa';
import appConfig from '../../../config/app-config';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;
const API_KEY = DATA_PROVIDER_ENV.key;
const BASE_URL = `${DATA_PROVIDER_ENV.address}/report-types/page-list`;

export type ReportTypeField = FieldItem;

type ReportType = {
    id: string;
    name: string;
    provider: string;
    description: string;
    fields: ReportTypeField[];
    params: Record<string, string>;
    createdAt: string;
    active: boolean;
};

const ReportTypePageList = () => {
    const navigate = useNavigate();
    const showFieldsModal = useSignal(false);
    const showParamsModal = useSignal(false);
    const currentFields = useSignal<ReportTypeField[]>([]);
    const currentParams = useSignal<Record<string, string>>({});
    const highlightedRowId = useSignal<string | null>(null);
    const {
        page = '0',
        pageSize = '20',
        field: fieldSorted = 'provider',
        order: orderSorted = 'ASC',
        filter,
    } = useParams();

    let description = '';
    if (filter) {
        [description = ''] = filter.match(/=(.*?);/g)?.map((m: string) => m.slice(1, -1)) ?? [''];
    }

    const url = `${BASE_URL}?search=${description}&page=${page}&pageSize=${pageSize}&order=${fieldSorted},${orderSorted}`;

    const renderRows = (rows: ReportType[]) => {
        if (!rows || rows.length === 0) {
            return (
                <tr>
                    <td colSpan={5} className="py-2 px-2 text-center">
                        <div className="text-2xl font-bold text-gray-500 mt-4">Nenhum registro encontrado...</div>
                    </td>
                </tr>
            );
        }

        return rows.map((row: ReportType, index: number) => (
            <tr
                key={row.id}
                className={`${index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-300 hover:shadow-md border-b border-gray-300 cursor-pointer ${highlightedRowId.value === row.id ? 'bg-gray-200' : ''}`}
                onClick={() => {
                    navigate(`/data-provider/report-type/view/${row.id}`, { replace: true });
                }}
            >
                <td className="py-2 px-2">{row.id}</td>
                <td className="py-2 px-2">{row.name}</td>
                <td className="py-2 px-2">{row.provider}</td>
                <td className="py-2 px-2">{row.description}</td>
                <td className="py-2 px-2 text-center">
                    {row.fields?.length > 0 ? (
                        <button
                            className="text-blue-600 underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                currentFields.value = row.fields;
                                showFieldsModal.value = true;
                                highlightedRowId.value = row.id;
                            }}
                        >
                            {row.fields.length}
                        </button>
                    ) : (
                        <span>{row.fields?.length ?? 0}</span>
                    )}
                </td>
                <td className="py-2 px-2 text-center">
                    {row.params && Object.keys(row.params).length > 0 ? (
                        <button
                            className="text-blue-600 underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                currentParams.value = row.params;
                                showParamsModal.value = true;
                                highlightedRowId.value = row.id;
                            }}
                        >
                            {Object.keys(row.params).length}
                        </button>
                    ) : (
                        <span>{Object.keys(row.params || {}).length}</span>
                    )}
                </td>
                <td className="py-2 px-2">{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ''}</td>
                <td className="py-2 px-2">
                    {row.active ? <FaCheck className="text-green-700 ml-8" /> : <FaTimes className='ml-8' />}
                </td>
            </tr>
        ));
    };

    const pageChanged = (pageNumber: number) => {
        pageNumber = pageNumber - 1;
        navigate(urlNavigate(pageNumber, Number(pageSize), fieldSorted, orderSorted, description), { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(urlNavigate(0, size, fieldSorted, orderSorted, description), { replace: true });
    };

    const pageSorted = (field: keyof ReportType | string, order: 'ASC' | 'DESC') => {
        navigate(urlNavigate(0, Number(pageSize), field, order, description), { replace: true });
    };

    const descriptionChanged = (value: string) => {
        navigate(urlNavigate(0, Number(pageSize), fieldSorted, orderSorted, value), { replace: true });
    };

    const onFilterClean = () => {
        navigate(urlNavigate(0, Number(pageSize), fieldSorted, orderSorted, ''), { replace: true });
    };

    const urlNavigate = (pageNumber: number, size: number, field: string, order: string, desc: string) => {
        return `/data-provider/report-type/${pageNumber}/${size}/${field}/${order}/d=${desc};`;
    };

    return (
        <>
            <PageList
                pageTitle="Tipos de relatório"
                url={url}
                fetchHeaders={[{ key: 'x-api-key', value: API_KEY }]}
                breadcrumb={[{ label: 'Tipos de relatório', url: '#' }]}
                tableHeaders={[
                    { id: 'col-id', field: 'id', title: '#', canSort: false, width: '100px' },
                    { id: 'col-name', field: 'name', title: 'Nome', canSort: false, width: '250px' },
                    { id: 'col-provider', field: 'provider', title: 'Provedor', canSort: false, width: '150px' },
                    { id: 'col-description', field: 'description', title: 'Descrição', canSort: false },
                    {
                        id: 'col-fields',
                        field: 'fields',
                        title: 'Campos',
                        canSort: false,
                        width: '90px',
                        headerTooltipDescription: 'Campos extraídos do payload do relatório',
                    },
                    { id: 'col-params', field: 'params', title: 'Parâmetros', canSort: false, width: '110px' },
                    { id: 'col-created', field: 'createdAt', title: 'Criado em', canSort: false, width: '100px' },
                    { id: 'col-active', field: 'active', title: 'Ativo', canSort: false, width: '80px' },
                ]}
                tableFieldSort={fieldSorted as string}
                tableOrderSort={orderSorted as 'ASC' | 'DESC'}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={Number(page)}
                pageSize={Number(pageSize)}
                onPageChange={pageChanged}
                onItemsPerPageChange={pageSizeChanged}
                defaultBackendHeader={false}
                itemsLabel="registros"
            >
                {{
                    filterSlot: (
                        <div className="grid grid-cols-12 gap-1">
                            <PageSearch
                                value={description}
                                label="Procurar por..."
                                onSearch={descriptionChanged}
                                filterCleanVisible={true}
                                filterCleanEnabled={Boolean(description)}
                                onFilterClean={onFilterClean}
                                configureVisible={false}
                                className="col-span-12"
                            />
                        </div>
                    ),
                    controlSlot: (
                        <div className="pt-4">
                            <button
                                type="button"
                                className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center"
                                onClick={() => navigate('/data-provider/report-type/edit', { replace: true })}
                            >
                                Novo cadastro
                            </button>
                        </div>
                    ),
                }}
            </PageList>
            <FieldsModal
                id="fields-modal"
                show={showFieldsModal.value}
                fields={currentFields.value}
                onClose={() => {
                    showFieldsModal.value = false;
                    highlightedRowId.value = null;
                }}
            />
            <ParamsModal
                id="params-modal"
                show={showParamsModal.value}
                params={currentParams.value}
                onClose={() => {
                    showParamsModal.value = false;
                    highlightedRowId.value = null;
                }}
            />
        </>
    );
};

export default ReportTypePageList;
